/**
 * AgencyTeam.jsx — Sprint 5 (Module 08).
 *
 * Admin-only: view teammates, invite new ones (email), update role, deactivate.
 * Pending invites surface above the team list with a revoke button.
 */
import React, { useEffect, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import {
  UserPlus, Crown, ShieldCheck, UserCircle, EnvelopeSimple,
  X, Check, CaretDown, ArrowsClockwise, PaperPlaneTilt,
} from '@phosphor-icons/react';

const ACCENT = '#6366f1';

const ROLES = [
  { id: 'admin',      label: 'Admin',      icon: Crown,       help: 'Full rights — manage SOWs, payments, team.' },
  { id: 'senior',     label: 'Senior',     icon: ShieldCheck, help: 'Draft SOWs + manage requests across clients.' },
  { id: 'junior',     label: 'Junior',     icon: UserCircle,  help: 'Move requests; cannot draft SOWs.' },
  { id: 'contractor', label: 'Contractor', icon: UserCircle,  help: 'External; assigned to specific requests.' },
];

const AgencyTeam = ({ user }) => {
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const isAdmin = user?.agency_role === 'admin';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/agency/team');
        if (!cancelled) {
          setMembers(r.data.members || []);
          setPending(r.data.pending_invites || []);
        }
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load team');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const updateRole = async (m, role) => {
    try {
      await api.patch(`/agency/team/${m.id}`, { agency_role: role });
      toast.success('Role updated');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const toggleActive = async (m) => {
    if (!window.confirm(`${m.agency_active === false ? 'Reactivate' : 'Deactivate'} ${m.full_name}?`)) return;
    try {
      if (m.agency_active === false) {
        await api.patch(`/agency/team/${m.id}`, { agency_active: true });
      } else {
        await api.delete(`/agency/team/${m.id}`);
      }
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const revokeInvite = async (inv) => {
    if (!window.confirm(`Revoke invite for ${inv.email}?`)) return;
    try {
      await api.delete(`/agency/team/invitations/${inv.id}`);
      toast.success('Revoked');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="p-12 text-sm text-text-muted inline-flex items-center gap-2"><ArrowsClockwise size={14} className="animate-spin" /> Loading…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1080px] mx-auto" data-testid="agency-team">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: ACCENT }}>Command Centre</div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary leading-[1.05] mb-2" style={{ letterSpacing: '-0.02em' }}>
            Team
          </h1>
          <p className="text-sm text-text-secondary">{members.length} teammate{members.length === 1 ? '' : 's'}{pending.length ? ` · ${pending.length} pending invite${pending.length === 1 ? '' : 's'}` : ''}</p>
        </div>
        {isAdmin && (
          <button onClick={() => setInviteOpen(true)} data-testid="agency-team-invite" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: ACCENT }}>
            <UserPlus size={13} weight="bold" /> Invite
          </button>
        )}
      </div>

      {/* Pending invites */}
      {pending.length > 0 && (
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-2 inline-flex items-center gap-1.5"><EnvelopeSimple size={11} weight="bold" /> Pending invites</div>
          <div className="space-y-2">
            {pending.map((inv) => (
              <div key={inv.id} className="bg-surface border border-border rounded-lg px-4 py-3 flex items-center justify-between" data-testid={`agency-pending-invite-${inv.id}`}>
                <div>
                  <div className="text-sm font-medium text-text-primary">{inv.email}</div>
                  <div className="text-[11px] text-text-muted capitalize">{inv.full_name || '—'} · {inv.agency_role} · invited by {inv.invited_by_name}</div>
                </div>
                {isAdmin && (
                  <button onClick={() => revokeInvite(inv)} data-testid={`agency-pending-revoke-${inv.id}`} className="text-xs text-text-muted hover:text-danger px-2 py-1 rounded">
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Members */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden" data-testid="agency-team-members">
        {members.map((m) => {
          const Icon = (ROLES.find((r) => r.id === m.agency_role) || ROLES[2]).icon;
          const isSelf = m.id === user?.id;
          const inactive = m.agency_active === false;
          return (
            <div key={m.id} className={`grid grid-cols-[1fr_140px_120px] gap-3 px-4 py-3 border-b border-border last:border-b-0 items-center ${inactive ? 'opacity-50' : ''}`} data-testid={`agency-team-member-${m.id}`}>
              <div>
                <div className="text-sm font-medium text-text-primary inline-flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full inline-flex items-center justify-center text-white text-[11px] font-bold" style={{ backgroundColor: ACCENT }}>
                    {(m.full_name || m.email).slice(0, 1).toUpperCase()}
                  </span>
                  {m.full_name} {isSelf && <span className="text-[10px] text-text-muted">(you)</span>}
                  {inactive && <span className="text-[9px] text-danger uppercase tracking-wider font-bold">deactivated</span>}
                </div>
                <div className="text-[11px] text-text-muted ml-9">{m.email}</div>
              </div>
              <div className="flex items-center gap-1">
                <Icon size={11} weight="bold" style={{ color: ACCENT }} />
                {isAdmin && !isSelf ? (
                  <select value={m.agency_role || 'junior'} onChange={(e) => updateRole(m, e.target.value)} data-testid={`agency-team-role-${m.id}`} className="bg-background border border-border rounded px-2 py-1 text-xs">
                    {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                ) : (
                  <span className="text-xs capitalize text-text-primary">{m.agency_role}</span>
                )}
              </div>
              <div className="text-right">
                {isAdmin && !isSelf && (
                  <button onClick={() => toggleActive(m)} data-testid={`agency-team-toggle-${m.id}`} className="text-xs text-text-muted hover:text-text-primary">
                    {inactive ? 'Reactivate' : 'Deactivate'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} onSent={() => { setInviteOpen(false); reload(); }} />}
    </div>
  );
};

const InviteModal = ({ onClose, onSent }) => {
  const [form, setForm] = useState({ email: '', full_name: '', agency_role: 'junior' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.email.trim() || !form.email.includes('@')) { toast.error('Valid email required'); return; }
    setBusy(true);
    try {
      await api.post('/agency/team/invitations', form);
      toast.success(`Invite sent to ${form.email}`);
      onSent();
    } catch (e) { toast.error(e.response?.data?.detail || 'Invite failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-start justify-center pt-16 p-4" onClick={onClose} data-testid="agency-team-invite-modal">
      <div className="bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-heading font-bold text-text-primary text-lg" style={{ letterSpacing: '-0.01em' }}>Invite teammate</h3>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary"><X size={16} weight="bold" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Email *</label>
            <input type="email" autoFocus value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} data-testid="agency-team-invite-email" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Full name</label>
            <input value={form.full_name} onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))} data-testid="agency-team-invite-name" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Role</label>
            <div className="relative">
              <select value={form.agency_role} onChange={(e) => setForm((p) => ({ ...p, agency_role: e.target.value }))} data-testid="agency-team-invite-role" className="appearance-none w-full bg-background border border-border rounded-lg pl-3 pr-8 py-2 text-sm">
                {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label} — {r.help}</option>)}
              </select>
              <CaretDown size={10} weight="bold" className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-border rounded-lg">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="agency-team-invite-submit" className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50 inline-flex items-center justify-center gap-2" style={{ backgroundColor: ACCENT }}>
            <PaperPlaneTilt size={12} weight="bold" /> {busy ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgencyTeam;
